'use client';

import { BarChart as RechartsBar, Bar, Cell, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  type ChartConfig,
} from '../chart';

export interface BarLegendItem {
  label: string;
  value: number;
  color: string;
}

export interface BarChartProps {
  data: Record<string, unknown>[];
  xKey: string;
  valueKey: string;
  label?: string;
  color?: string;
  colors?: string[];
  legendItems?: BarLegendItem[];
  height?: number;
  className?: string;
  /** 'horizontal' (default) draws vertical bars; 'vertical' draws horizontal bars. Mirrors recharts' `layout` prop naming. */
  layout?: 'horizontal' | 'vertical';
}

export function BarChart({
  data,
  xKey,
  valueKey,
  label,
  color = 'hsl(var(--chart-1))',
  colors,
  legendItems,
  height,
  className,
  layout = 'horizontal',
}: BarChartProps) {
  const { t } = useTranslation('ui');
  const config: ChartConfig = {
    [valueKey]: { label: label ?? t('common:fields.value'), color },
  };

  // Category YAxis (vertical/horizontal-bars layout) needs enough width for
  // the longest label or Recharts silently clips it — size it to content
  // instead of a fixed guess, clamped to a sane range.
  const longestLabel = data.reduce((max, row) => {
    const text = String(row[xKey] ?? '');
    return text.length > max.length ? text : max;
  }, '');
  const categoryAxisWidth = Math.min(160, Math.max(72, longestLabel.length * 7 + 24));

  const LegendContent = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 16px',
        paddingTop: 8,
      }}
    >
      {legendItems!.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: item.color,
              flexShrink: 0,
            }}
          />
          <span className="text-[var(--color-text-secondary,#71717a)]">{item.label}</span>
          <span className="font-bold text-[var(--color-text-primary,#18181b)]">{item.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <ChartContainer
      config={config}
      className={cn('w-full aspect-auto!', className)}
      style={height ? { height } : undefined}
    >
      <RechartsBar data={data} layout={layout} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid
          horizontal={layout === 'horizontal'}
          vertical={layout === 'vertical'}
          strokeDasharray="3 3"
        />
        {layout === 'vertical' ? (
          <>
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey={xKey}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={categoryAxisWidth}
            />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickMargin={8} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} width={36} />
          </>
        )}
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar dataKey={valueKey} radius={[4, 4, 0, 0]} fill={color}>
          {colors && data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
        </Bar>
        {legendItems && legendItems.length > 0 && <ChartLegend content={() => <LegendContent />} />}
      </RechartsBar>
    </ChartContainer>
  );
}
